import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gpuAPI } from '../../services/api';

const FRAMEWORKS = [
  { id:'python',     label:'Python',      desc:'numpy, pandas, scikit-learn, jupyter' },
  { id:'pytorch',    label:'PyTorch',     desc:'torch, torchvision, torchaudio' },
  { id:'tensorflow', label:'TensorFlow',  desc:'tensorflow-gpu' },
  { id:'nodejs',     label:'Node.js',     desc:'nodejs, npm' },
  { id:'java',       label:'Java',        desc:'default-jdk, maven' },
];

const DATABASES = [
  { id:'postgres', label:'PostgreSQL', desc:'psql client + sidecar pod' },
  { id:'redis',    label:'Redis',      desc:'redis-cli + sidecar pod' },
  { id:'mongodb',  label:'MongoDB',    desc:'mongosh + sidecar pod' },
  { id:'mysql',    label:'MySQL',      desc:'mysql client + sidecar pod' },
];

const LLM_MODELS = [
  { value:'',                             label:'None' },
  { value:'google/gemma-4-26B-A4B-it',   label:'Gemma 4 26B (pre-installed on NVMe)' },
  { value:'meta-llama/Llama-3.1-8B',     label:'Llama 3.1 8B' },
  { value:'mistralai/Mistral-7B-v0.1',   label:'Mistral 7B' },
  { value:'custom',                       label:'Custom model path' },
];

export default function GpuInstanceLaunchPage() {
  const navigate = useNavigate();
  const [servers, setServers]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError]         = useState('');
  const [result, setResult]       = useState(null);

  const [form, setForm] = useState({
    gpu_server_id: '',
    gpu_count: 1,
    instance_name: '',
    docker_image: 'python:3.11-slim',
    custom_image: false,
    frameworks: [],
    databases: [],
    extra_packages: '',
    llm_model: '',
    custom_llm: '',
    subdomain: '',
    app_port: '',
    duration_days: 365,
    vcpu: '',
    ram_gb: '',
    storage_gb: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await gpuAPI.listServers();
      setServers(r.data || []);
      if (r.data?.length) setForm(f => ({...f, gpu_server_id: r.data[0].id}));
    } catch (e) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({...f, [k]: v}));

  const toggleList = (field, val) => setForm(f => ({
    ...f,
    [field]: f[field].includes(val) ? f[field].filter(x => x !== val) : [...f[field], val],
  }));

  const selectedServer = servers.find(s => s.id === parseInt(form.gpu_server_id));

  const handleLaunch = async () => {
    if (!form.gpu_server_id) { setError('Select a GPU server'); return; }
    if (!form.instance_name.trim()) { setError('Instance name is required'); return; }
    setLaunching(true); setError('');
    try {
      const llm = form.llm_model === 'custom' ? form.custom_llm : form.llm_model;
      const r = await gpuAPI.launchInstance({
        gpu_server_id:  parseInt(form.gpu_server_id),
        gpu_count:      parseInt(form.gpu_count),
        instance_name:  form.instance_name.trim(),
        docker_image:   form.custom_image ? form.docker_image : (
          form.frameworks.includes('pytorch')    ? 'python:3.11-slim' :
          form.frameworks.includes('tensorflow') ? 'python:3.11-slim' : 'python:3.11-slim'
        ),
        frameworks:     form.frameworks,
        databases:      form.databases,
        extra_packages: form.extra_packages,
        llm_model:      llm,
        subdomain:      form.subdomain.toLowerCase().replace(/[^a-z0-9-]/g,''),
        app_port:       parseInt(form.app_port) || null,
        duration_days:  parseInt(form.duration_days) || 365,
        vcpu:           parseInt(form.vcpu) || null,
        ram_gb:         parseInt(form.ram_gb) || null,
        storage_gb:     parseInt(form.storage_gb) || null,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Launch failed');
    } finally { setLaunching(false); }
  };

  const lbl = (text, hint) => (
    <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:5 }}>
      {text}{hint && <span style={{ fontWeight:400, color:'var(--muted-2)', marginLeft:6 }}>{hint}</span>}
    </label>
  );
  const card = (title, children, optional=false) => (
    <section className="card">
      <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:18,
        paddingBottom:10, borderBottom:'1px solid var(--surface-2)', display:'flex', gap:8, alignItems:'center' }}>
        {title} {optional && <span style={{ fontSize:11, fontWeight:400, color:'var(--muted-2)' }}>optional</span>}
      </div>
      {children}
    </section>
  );

  // ── Result page after successful launch ──────────────────────────────────────
  if (result) {
    return (
      <div style={{ padding:'24px 32px', maxWidth:720 }}>
        <div className="banner success" style={{ marginBottom:24, flexDirection:'column' }}>
          <div style={{ fontSize:16, fontWeight:700, color:'var(--color-success-700)', marginBottom:8 }}>
            ✅ GPU Instance Launched!
          </div>
          <div style={{ fontSize:13, color:'var(--color-success-700)' }}>
            Your instance is provisioning. SSH will be ready in ~2 minutes.
          </div>
        </div>

        <div style={{ background:'#0d1117', borderRadius:10, padding:24, marginBottom:16 }}>
          <div style={{ fontSize:12, color:'var(--muted-2)', marginBottom:12, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>SSH Access</div>
          <div style={{ fontFamily:'monospace', fontSize:14, color:'var(--brand)', marginBottom:8 }}>
            {result.connect_cmd}
          </div>
          <div style={{ display:'flex', gap:24, fontSize:12, color:'var(--muted-2)' }}>
            <span>Password: <code style={{ color:'var(--accent)' }}>{result.ssh_password}</code></span>
            <span>Port: <code style={{ color:'var(--color-success-500)' }}>{result.ssh_port}</code></span>
          </div>
        </div>

        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Instance Details</div>
          {[
            ['GPU',         `${result.gpu_count}x ${result.gpu_model || 'NVIDIA H100'}`],
            ['Instance',    result.instance_name || result.container_name],
            ['SSH Host',    result.ssh_host],
            ['Expires',     result.expires_at ? new Date(result.expires_at).toLocaleDateString() : '1 year'],
            ['LLM Model',   result.llm_model_path || 'None'],
            ...(result.domain_url ? [['Domain', result.domain_url]] : []),
          ].map(([k,v]) => v && (
            <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0',
              borderBottom:'1px solid var(--surface-2)', fontSize:12 }}>
              <span style={{ color:'var(--muted)' }}>{k}</span>
              <span style={{ color:'var(--ink)', fontFamily:'monospace', fontSize:11 }}>{v}</span>
            </div>
          ))}
        </div>

        {result.databases && Object.keys(result.databases).length > 0 && (
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>
              Database Connections (available inside your instance)
            </div>
            {Object.entries(result.databases).map(([db, url]) => (
              <div key={db} style={{ marginBottom:8 }}>
                <div style={{ fontSize:11, color:'var(--muted-2)', fontWeight:600, textTransform:'uppercase' }}>{db}</div>
                <code style={{ fontSize:11, color:'var(--brand)', wordBreak:'break-all' }}>{url}</code>
              </div>
            ))}
          </div>
        )}

        {result.warning && (
          <div className="banner warning" style={{ marginBottom:16 }}>
            ⚠ {result.warning}
          </div>
        )}

        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => navigate('/gpu')} className="btn btn-primary">
            View in GPU Servers
          </button>
          <button onClick={() => { setResult(null); setForm(f => ({...f, instance_name:''})); }}
            className="btn btn-secondary">
            Launch Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:'24px 32px', maxWidth:820 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:28 }}>
        <button onClick={() => navigate('/gpu')}
          style={{ background:'none', border:'none', color:'var(--brand)', fontSize:13, cursor:'pointer', fontWeight:500 }}>
          ← GPU Servers
        </button>
        <span style={{ color:'var(--muted-2)' }}>›</span>
        <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:'var(--ink)' }}>
          ⚡ Launch GPU Instance
        </h1>
      </div>

      {loading ? (
        <div style={{ color:'var(--muted-2)', padding:40, textAlign:'center' }}>Loading GPU servers...</div>
      ) : servers.length === 0 ? (
        <div className="banner danger">
          No GPU servers registered. Ask an admin to register a GPU server first.
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Section 1: Basic */}
          {card('1 · Instance', <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div>
                {lbl('Instance Name', '*')}
                <input className="input" placeholder="my-gpu-instance" value={form.instance_name}
                  onChange={e => setF('instance_name', e.target.value)} />
              </div>
              <div>
                {lbl('GPU Server', '*')}
                <select className="select" value={form.gpu_server_id}
                  onChange={e => setF('gpu_server_id', e.target.value)}>
                  {servers.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.server_name} — {s.gpu_model} ({s.instances_available} GPUs free)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* GPU count + server info */}
            {selectedServer && (
              <div style={{ marginTop:16 }}>
                {lbl('GPU Count')}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {[1,2,4,8].filter(n => n <= selectedServer.instances_available).map(n => (
                    <button key={n} onClick={() => setF('gpu_count', n)}
                      style={{ padding:'8px 20px', fontSize:13, fontWeight:600, borderRadius:7,
                        cursor:'pointer', border:'1.5px solid', transition:'all 0.15s',
                        background: form.gpu_count===n ? 'var(--accent)' : 'var(--surface)',
                        color:      form.gpu_count===n ? '#fff' : 'var(--ink-2)',
                        borderColor:form.gpu_count===n ? 'var(--accent)' : 'var(--border)' }}>
                      {n} GPU{n>1?'s':''} · {(selectedServer.total_vram_gb/selectedServer.gpu_count*n).toFixed(0)}GB VRAM
                    </button>
                  ))}
                </div>
                <div style={{ marginTop:8, padding:'8px 12px', background:'var(--accent-soft)', borderRadius:6,
                  fontSize:12, color:'var(--accent-ink)' }}>
                  {selectedServer.gpu_model} · {selectedServer.instances_available} of {selectedServer.instances_total} GPUs available
                </div>
              </div>
            )}
          </>)}

          {/* Section 2: Image */}
          {card('2 · Container Image', <>
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
              {[
                ['python:3.11-slim', 'Python 3.11', 'Small, fast (~200MB)'],
                ['pytorch/pytorch:2.1.0-cuda12.1-cudnn8-runtime', 'PyTorch 2.1', 'CUDA 12.1 runtime (~5GB)'],
                ['tensorflow/tensorflow:2.14.0-gpu', 'TensorFlow 2.14', 'GPU build (~4GB)'],
                ['custom', 'Custom', 'Enter your own image'],
              ].map(([val, label, desc]) => (
                <button key={val}
                  onClick={() => {
                    if (val === 'custom') { setF('custom_image', true); }
                    else { setF('custom_image', false); setF('docker_image', val); }
                  }}
                  style={{ flex:1, padding:'10px 8px', fontSize:12, fontWeight:600, borderRadius:7,
                    cursor:'pointer', border:'1.5px solid', transition:'all 0.15s', textAlign:'left',
                    background: (!form.custom_image && form.docker_image===val) || (form.custom_image && val==='custom') ? 'var(--brand)' : 'var(--surface)',
                    color:      (!form.custom_image && form.docker_image===val) || (form.custom_image && val==='custom') ? '#fff' : 'var(--ink-2)',
                    borderColor:(!form.custom_image && form.docker_image===val) || (form.custom_image && val==='custom') ? 'var(--brand)' : 'var(--border)' }}>
                  <div>{label}</div>
                  <div style={{ fontSize:10, fontWeight:400, opacity:0.8, marginTop:2 }}>{desc}</div>
                </button>
              ))}
            </div>
            {form.custom_image && (
              <input className="input" placeholder="registry/image:tag" value={form.docker_image}
                onChange={e => setF('docker_image', e.target.value)} />
            )}
          </>)}

          {/* Section 3: Frameworks */}
          {card('3 · Frameworks', <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {FRAMEWORKS.map(fw => (
                <label key={fw.id} style={{ display:'flex', alignItems:'flex-start', gap:10,
                  padding:'10px 12px', border:'1px solid', borderRadius:8, cursor:'pointer',
                  background: form.frameworks.includes(fw.id) ? 'var(--brand-soft)' : 'var(--surface)',
                  borderColor: form.frameworks.includes(fw.id) ? 'var(--brand)' : 'var(--border)',
                  transition:'all 0.15s' }}>
                  <input type="checkbox" checked={form.frameworks.includes(fw.id)}
                    onChange={() => toggleList('frameworks', fw.id)}
                    style={{ marginTop:2, accentColor:'var(--brand)' }} />
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>{fw.label}</div>
                    <div style={{ fontSize:11, color:'var(--muted-2)', marginTop:1 }}>{fw.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ marginTop:12 }}>
              {lbl('Extra Packages', '(pip or apt, space/comma separated)')}
              <input className="input" placeholder="transformers accelerate sentencepiece" value={form.extra_packages}
                onChange={e => setF('extra_packages', e.target.value)} />
            </div>
          </>, true)}

          {/* Section 4: Databases */}
          {card('4 · Databases', <>
            <div style={{ marginBottom:8, fontSize:12, color:'var(--muted)' }}>
              Each selected database creates a sidecar pod in the same namespace.
              Connection env vars are pre-configured in your SSH session.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {DATABASES.map(db => (
                <label key={db.id} style={{ display:'flex', alignItems:'flex-start', gap:10,
                  padding:'10px 12px', border:'1px solid', borderRadius:8, cursor:'pointer',
                  background: form.databases.includes(db.id) ? 'var(--brand-soft)' : 'var(--surface)',
                  borderColor: form.databases.includes(db.id) ? 'var(--brand)' : 'var(--border)' }}>
                  <input type="checkbox" checked={form.databases.includes(db.id)}
                    onChange={() => toggleList('databases', db.id)}
                    style={{ marginTop:2, accentColor:'var(--brand)' }} />
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>{db.label}</div>
                    <div style={{ fontSize:11, color:'var(--muted-2)', marginTop:1 }}>{db.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </>, true)}

          {/* Section 5: LLM Model */}
          {card('5 · LLM Model', <>
            <div>
              {lbl('Pre-installed model on NVMe', '(select if already downloaded on the GPU server)')}
              <select className="select" value={form.llm_model} onChange={e => setF('llm_model', e.target.value)}>
                {LLM_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {form.llm_model === 'custom' && (
              <div style={{ marginTop:12 }}>
                {lbl('Custom model path', '(HuggingFace model ID or NVMe path)')}
                <input className="input" placeholder="org/model-name or /opt/dlami/nvme/models/..." value={form.custom_llm}
                  onChange={e => setF('custom_llm', e.target.value)} />
              </div>
            )}
            {form.llm_model && form.llm_model !== 'custom' && (
              <div className="banner success" style={{ marginTop:8 }}>
                ✓ Model env vars will be pre-set: <code>LLM_MODEL_PATH</code>, <code>HF_HOME</code>, <code>TRANSFORMERS_CACHE</code>
              </div>
            )}
          </>, true)}

          {/* Section 5b: Resources (CPU/RAM/Storage for the GPU instance) */}
          {card('Resources (optional)', <>
            <div style={{ fontSize:12, color:'#64748b', marginBottom:10 }}>
              CPU, RAM &amp; storage for the GPU instance. The GPU instance is a lightweight GPU
              endpoint — your app runs on a CPU instance and connects to it. Leave blank
              to auto-size per GPU (≈ {form.gpu_count||1}×8 vCPU / {(form.gpu_count||1)*32} GB limit).
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
              <div>
                {lbl('vCPU cores', '(optional)')}
                <input className="input" type="number" min="1" max="192" placeholder={`auto (${(form.gpu_count||1)*8})`}
                  value={form.vcpu} onChange={e => setF('vcpu', e.target.value)} />
              </div>
              <div>
                {lbl('RAM (GB)', '(optional)')}
                <input className="input" type="number" min="1" max="1900" placeholder={`auto (${(form.gpu_count||1)*32})`}
                  value={form.ram_gb} onChange={e => setF('ram_gb', e.target.value)} />
              </div>
              <div>
                {lbl('Storage (GB)', '(optional)')}
                <input className="input" type="number" min="1" max="20000" placeholder="auto (100)"
                  value={form.storage_gb} onChange={e => setF('storage_gb', e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:8 }}>
              Persistent /app volume, provisioned via the <code>local-storage</code> StorageClass
              (retained on delete). Default 100 GB; max 20 TB.
            </div>
          </>, true)}

          {/* Section 6: Networking */}
          {card('6 · Networking', <>
            <div style={{ fontSize:11, color:'var(--muted-2)', lineHeight:1.5 }}>
              To expose extra ports, launch the instance, then go to
              <b> Network → Services</b> and create a Service bound to this instance
              (choose the instance; set internal &amp; external ports).
            </div>
            <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div>
                {lbl('Subdomain', '(optional — creates public HTTPS URL)')}
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <input className="input" placeholder="myapp" value={form.subdomain}
                    onChange={e => setF('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,''))}
                    style={{ maxWidth:160 }} />
                  <span style={{ fontSize:12, color:'var(--muted-2)', whiteSpace:'nowrap' }}>.kube.sarv.com</span>
                </div>
              </div>
              <div>
                {lbl('App Port', '(port your app listens on, for ingress)')}
                <input className="input" type="number" placeholder="8000" value={form.app_port}
                  onChange={e => setF('app_port', e.target.value)} />
              </div>
            </div>
          </>, true)}

          {/* Error & Launch */}
          {error && (
            <div className="banner danger">
              {error}
            </div>
          )}

          <div style={{ display:'flex', gap:12, paddingBottom:40 }}>
            <button onClick={handleLaunch} disabled={launching} className="btn btn-accent btn-lg">
              {launching ? '⟳ Launching... (may take 1-2 min)' : '⚡ Launch GPU Instance'}
            </button>
            <button onClick={() => navigate('/gpu')} className="btn btn-secondary btn-lg">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
